import React, { useState, useEffect } from 'react';
import s from './s.module.less';
import OnboardingStepContainer from '../OnboardingStepContainer';
import RadioOption from '../RadioOption';
import ActionButtons from '../ActionButtons';
import { Input } from 'antd';

interface ReferralSourceStepProps {
    onReferralSourceChange?: (referralSource: string) => void;
    onBack?: () => void;
    onNext?: () => void;
    currentStep?: number;
    totalSteps?: number;
    initialReferralSource?: string;
    initialOthersText?: string;
}

const referralSourceOptions = [
    { id: 'online-search', label: 'Online search (Google, Bing, etc.)' },
    { id: 'job-posting', label: 'Job posting (Indeed, ZipRecruiter, etc.)' },
    { id: 'provider-referral', label: 'Referral from another provider' },
    { id: 'klarity-outreach', label: 'Klarity direct outreach' },
    { id: 'healthcare-brands', label: 'Zocdoc, Headway or other healthcare brands' },
    { id: 'others', label: 'Others' },
];

export default function ReferralSourceStep({
    onReferralSourceChange,
    onBack,
    onNext,
    currentStep = 6,
    totalSteps = 7,
    initialReferralSource = '',
    initialOthersText = '',
}: ReferralSourceStepProps) {
    const [selectedSource, setSelectedSource] = useState<string>('');
    const [othersText, setOthersText] = useState<string>('');

    // 处理初始数据回显逻辑
    useEffect(() => {
        if (!initialReferralSource) {
            return;
        }

        const predefinedSourceIds = referralSourceOptions.map((option) => option.id);

        if (predefinedSourceIds.includes(initialReferralSource)) {
            // 如果是预定义的选项，直接设置
            setSelectedSource(initialReferralSource);
        } else {
            // 如果不是预定义的选项，说明是others的自定义内容
            setSelectedSource('others');
            setOthersText(initialReferralSource);
        }

        // 如果有初始Others文本，也设置到othersText中
        if (initialOthersText) {
            setOthersText(initialOthersText);
        }
    }, [initialReferralSource, initialOthersText]);

    const handleSourceChange = (sourceId: string) => {
        setSelectedSource(sourceId);

        // 如果取消选择 "others"，清空输入文本
        if (sourceId !== 'others') {
            setOthersText('');
        }

        // 如果选择了非others选项，直接进入下一步
        if (sourceId !== 'others') {
            if (onReferralSourceChange) {
                onReferralSourceChange(sourceId);
            }
            if (onNext) {
                onNext();
            }
        }
    };

    const handleOthersTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setOthersText(newText);
    };

    // 验证表单是否可以提交
    const isFormValid = () => {
        if (!selectedSource) {
            return false;
        }

        // 如果选择了others，必须填写内容
        if (selectedSource === 'others' && !othersText.trim()) {
            return false;
        }

        return true;
    };

    // 检查是否应该显示Next按钮 - 只有选择了others时才显示
    const shouldShowNext = () => {
        return selectedSource === 'others';
    };

    const handleNext = () => {
        if (!isFormValid()) {
            return;
        }

        // 处理数据：如果选择了others，用input的值替换"others"
        const finalSource = selectedSource === 'others' && othersText.trim()
            ? othersText.trim()
            : selectedSource;

        // 调用父组件的回调函数
        if (onReferralSourceChange) {
            onReferralSourceChange(finalSource);
        }

        // 调用原本的onNext函数
        if (onNext) {
            onNext();
        }
    };

    return (
        <>
            <OnboardingStepContainer
                currentStep={currentStep}
                totalSteps={totalSteps}
                title="How did you hear about Klarity?"
                subtitle="Help us understand how you found Klarity."
                showBackButton
                onBack={onBack}
            >
                <div className={s.optionsContainer}>
                    {referralSourceOptions.map((option) => (
                        <RadioOption
                            key={option.id}
                            id={option.id}
                            label={option.label}
                            checked={selectedSource === option.id}
                            onChange={handleSourceChange}
                        />
                    ))}

                    {/* Others 输入框 - 当选择了 others 时显示 */}
                    {selectedSource === 'others' && (
                        <div className={s.othersInputContainer}>
                            <label htmlFor="others-input" className={s.othersLabel}>
                                Please specify
                            </label>
                            <Input
                                id="others-input"
                                type="text"
                                value={othersText}
                                onChange={handleOthersTextChange}
                                placeholder="Please tell us how you heard about Klarity..."
                                className={s.othersInput}
                            />
                        </div>
                    )}
                </div>
            </OnboardingStepContainer>

            <ActionButtons
                showBack
                showNext={shouldShowNext()}
                onNext={handleNext}
                nextDisabled={!isFormValid()}
                currentStep={currentStep}
                onBack={onBack}
            />
        </>
    );
}
